import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { AuthContext } from '@/hooks/use-auth';
import type {
  UserSession, PortalType 
} from '@/types/auth';
import {
  cognitoSignIn,
  cognitoSignOut,
  getCurrentCognitoUser,
  completeNewPassword,
  type SignInResult,
  type CognitoUser,
} from '@/lib/cognito-client';

interface AuthProviderProps {
  readonly children: ReactNode;
  readonly portalType: PortalType;
}

const isDev = import.meta.env.DEV;
const useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

function mapCognitoUserToSession(cognitoUser: CognitoUser): UserSession {
  return {
    userId: cognitoUser.userId,
    email: cognitoUser.email,
    organizationId: '',
    groups: cognitoUser.groups,
    siteAccess: [],
    venueAccess: [],
    supplierId: cognitoUser.supplierId,
    givenName: cognitoUser.givenName,
    familyName: cognitoUser.familyName,
    phoneNumber: cognitoUser.phoneNumber,
    jobTitle: cognitoUser.jobTitle,
    preferredLanguage: cognitoUser.preferredLanguage,
  };
}

// Custom error class for auth errors
class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

export type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | {
    status: 'authenticated';
    user: UserSession 
  }
  | {
    status: 'newPasswordRequired';
    username: string 
  }
  | {
    status: 'mfaRequired';
    challengeName: string 
  };

export function AuthProvider({
  children, portalType 
}: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({ status: 'loading' });

  // Derived state for backward compatibility
  const user = authState.status === 'authenticated' ? authState.user : null;
  const isLoading = authState.status === 'loading';
  const isAuthenticated = authState.status === 'authenticated';
  const requiresNewPassword = authState.status === 'newPasswordRequired';
  const pendingUsername =
    authState.status === 'newPasswordRequired' ? authState.username : null;

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (isDev && useMockAuth) {
          setAuthState({ status: 'unauthenticated' });
        } else {
          const cognitoUser = await getCurrentCognitoUser();
          if (cognitoUser?.supplierId) {
            setAuthState({
              status: 'authenticated',
              user: mapCognitoUserToSession(cognitoUser),
            });
          } else {
            setAuthState({ status: 'unauthenticated' });
          }
        }
      } catch {
        setAuthState({ status: 'unauthenticated' });
      }
    };

    void checkAuth();
  }, []);

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      setAuthState({ status: 'loading' });
      try {
        if (isDev && useMockAuth) {
          if (!email || !password) {
            throw new AuthError('Email and password are required');
          }

          await new Promise((resolve) => setTimeout(resolve, 500));

          const mockUser: UserSession = {
            userId: 'mock-supplier-001',
            email,
            organizationId: '',
            groups: ['supplier-admin'],
            siteAccess: [],
            venueAccess: [],
            supplierId: 'sup_sysco_demo',
            givenName: 'Supplier',
            familyName: 'Admin',
            jobTitle: 'Administrator',
            preferredLanguage: 'en',
          };

          setAuthState({
            status: 'authenticated',
            user: mockUser 
          });

          const cognitoUser: CognitoUser = {
            userId: mockUser.userId,
            email: mockUser.email,
            groups: mockUser.groups,
            supplierId: mockUser.supplierId,
            givenName: mockUser.givenName,
            familyName: mockUser.familyName,
            jobTitle: mockUser.jobTitle,
            preferredLanguage: mockUser.preferredLanguage,
          };

          return {
            status: 'success',
            user: cognitoUser 
          };
        }

        const result = await cognitoSignIn(email, password);

        if (result.status === 'success') {
          setAuthState({
            status: 'authenticated',
            user: mapCognitoUserToSession(result.user),
          });
        } else if (result.status === 'newPasswordRequired') {
          setAuthState({
            status: 'newPasswordRequired',
            username: result.username,
          });
        } else if (result.status === 'mfaRequired') {
          setAuthState({
            status: 'mfaRequired',
            challengeName: result.challengeName,
          });
        }

        return result;
      } catch (error) {
        setAuthState({ status: 'unauthenticated' });
        throw error;
      }
    },
    []
  );

  const setNewPassword = useCallback(
    async (newPassword: string): Promise<void> => {
      if (authState.status !== 'newPasswordRequired') {
        throw new AuthError('Not in new password required state');
      }

      setAuthState({ status: 'loading' });
      try {
        const cognitoUser = await completeNewPassword(newPassword);
        setAuthState({
          status: 'authenticated',
          user: mapCognitoUserToSession(cognitoUser),
        });
      } catch (error) {
        setAuthState({
          status: 'newPasswordRequired',
          username: authState.username,
        });
        throw error;
      }
    },
    [authState]
  );

  const signOut = useCallback(async () => {
    setAuthState({ status: 'loading' });
    try {
      if (!(isDev && useMockAuth)) {
        await cognitoSignOut();
      }
      setAuthState({ status: 'unauthenticated' });
    } catch {
      setAuthState({ status: 'unauthenticated' });
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      portalType,
      signIn,
      signOut,
      requiresNewPassword,
      pendingUsername,
      setNewPassword,
    }),
    [
      user,
      isAuthenticated,
      isLoading,
      portalType,
      signIn,
      signOut,
      requiresNewPassword,
      pendingUsername,
      setNewPassword,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
